import React, { useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import '../styles/BrandingPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const MAX_GALLERY_PHOTOS = 20;

export default function BrandingPage() {
  const { account: authAccount } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accountId, setAccountId] = useState(null);
  const [salonTitle, setSalonTitle] = useState('');

  const [logoKey, setLogoKey] = useState(Date.now());
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoDragging, setLogoDragging] = useState(false);
  const logoInputRef = useRef(null);

  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryDragging, setGalleryDragging] = useState(false);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/backoffice/account');
        if (data.success) {
          const a = data.account;
          setAccountId(a.id);
          setSalonTitle(a.title || authAccount?.title || '');
        }
      } catch {
        toast.error('خطأ في تحميل بيانات الحساب');
      } finally {
        setLoading(false);
      }
    })();
  }, [authAccount?.title]);

  const loadGallery = useCallback(async () => {
    try {
      const { data } = await api.get('/backoffice/gallery');
      if (data.success) {
        setGalleryPhotos(data.photos || []);
      }
    } catch (err) {
      console.error('Failed to load gallery', err);
    }
  }, []);

  useEffect(() => {
    if (accountId) loadGallery();
  }, [accountId, loadGallery]);

  const currentLogoUrl = accountId
    ? `${API_BASE_URL}/files/${accountId}?v=${logoKey}`
    : null;

  const handleLogoFileSelect = useCallback((file) => {
    if (!file) return;
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('صيغة غير مدعومة. استخدم PNG أو JPG أو WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم الصورة يتجاوز 5 ميغابايت');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
  }, []);

  const handleLogoUpload = useCallback(async (file) => {
    if (!file) return;
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('logo', file);
    try {
      const { data } = await api.post('/backoffice/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        toast.success('تم رفع الشعار بنجاح');
        setLogoKey(Date.now());
        setLogoPreview(null);
      } else {
        toast.error(data.message || 'فشل رفع الشعار');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل رفع الشعار');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  }, []);

  const handleLogoInputChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleLogoFileSelect(file);
      handleLogoUpload(file);
    }
  }, [handleLogoFileSelect, handleLogoUpload]);

  const handleLogoDrop = useCallback((e) => {
    e.preventDefault();
    setLogoDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleLogoFileSelect(file);
      handleLogoUpload(file);
    }
  }, [handleLogoFileSelect, handleLogoUpload]);

  const handleDeleteLogo = useCallback(async () => {
    if (!window.confirm('هل تريد حذف الشعار الحالي؟')) return;
    try {
      const { data } = await api.delete('/backoffice/logo');
      if (data.success) {
        toast.success('تم حذف الشعار');
        setLogoKey(Date.now());
        setLogoPreview(null);
      } else {
        toast.error(data.message || 'فشل حذف الشعار');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل حذف الشعار');
    }
  }, []);

  const handleGalleryUpload = useCallback(async (files) => {
    if (!files?.length) return;
    const remaining = MAX_GALLERY_PHOTOS - galleryPhotos.length;
    if (remaining <= 0) {
      toast.error(`الحد الأقصى ${MAX_GALLERY_PHOTOS} صورة`);
      return;
    }
    const toUpload = Array.from(files).slice(0, remaining);
    if (toUpload.length < files.length) {
      toast.warn(`سيتم رفع ${toUpload.length} صورة فقط (الحد الأقصى ${MAX_GALLERY_PHOTOS})`);
    }

    setGalleryUploading(true);
    let uploaded = 0;
    for (const file of toUpload) {
      const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!allowed.includes(file.type)) {
        toast.error(`${file.name}: صيغة غير مدعومة`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: حجم يتجاوز 5 ميغابايت`);
        continue;
      }
      const formData = new FormData();
      formData.append('photo', file);
      try {
        const { data } = await api.post('/backoffice/gallery', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data.success) uploaded++;
        else toast.error(data.message || `فشل رفع ${file.name}`);
      } catch (err) {
        toast.error(err.response?.data?.message || `فشل رفع ${file.name}`);
      }
    }
    if (uploaded > 0) {
      toast.success(`تم رفع ${uploaded} صورة`);
      await loadGallery();
    }
    setGalleryUploading(false);
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  }, [galleryPhotos.length, loadGallery]);

  const handleGalleryInputChange = useCallback((e) => {
    handleGalleryUpload(e.target.files);
  }, [handleGalleryUpload]);

  const handleGalleryDrop = useCallback((e) => {
    e.preventDefault();
    setGalleryDragging(false);
    handleGalleryUpload(e.dataTransfer.files);
  }, [handleGalleryUpload]);

  const handleDeleteGalleryPhoto = useCallback(async (photoId) => {
    if (!window.confirm('هل تريد حذف هذه الصورة؟')) return;
    try {
      const { data } = await api.delete(`/backoffice/gallery/${photoId}`);
      if (data.success) {
        toast.success('تم حذف الصورة');
        await loadGallery();
      } else {
        toast.error(data.message || 'فشل حذف الصورة');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'فشل حذف الصورة');
    }
  }, [loadGallery]);

  if (loading) return <div className="page-loading">جاري التحميل...</div>;

  if (!accountId) {
    return (
      <div className="branding-page">
        <h1>الشعار والمعرض</h1>
        <p className="branding-page-sub">تعذر تحميل بيانات الحساب</p>
      </div>
    );
  }

  return (
    <div className="branding-page">
      <h1>الشعار والمعرض</h1>
      <p className="branding-page-sub">شعار الصالون وصور الألبوم كما تظهر في صفحة الحجز</p>

      <section className="logo-section">
        <h2>شعار الصالون</h2>
        <div className="logo-card">
          <div className="logo-preview-wrap">
            {(logoPreview || currentLogoUrl) ? (
              <img
                key={logoPreview ? 'preview' : logoKey}
                src={logoPreview || currentLogoUrl}
                alt="شعار الصالون"
                className="logo-preview-img"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <span className="logo-preview-placeholder">
                {salonTitle?.charAt(0) || '?'}
              </span>
            )}
          </div>

          <div className="logo-upload-area">
            <div
              className={`logo-dropzone${logoDragging ? ' dragging' : ''}`}
              onClick={() => logoInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setLogoDragging(true); }}
              onDragLeave={() => setLogoDragging(false)}
              onDrop={handleLogoDrop}
              role="button"
              tabIndex={0}
              aria-label="منطقة رفع الشعار"
              onKeyDown={(e) => e.key === 'Enter' && logoInputRef.current?.click()}
            >
              <svg className="logo-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <rect x="3" y="3" width="18" height="18" rx="3" />
                <path d="M3 15l5-5 4 4 3-3 6 6" />
                <circle cx="8.5" cy="8.5" r="1.5" />
              </svg>
              <p className="logo-dropzone-text">
                {logoUploading ? 'جاري الرفع...' : 'اسحب صورة هنا أو اضغط للاختيار'}
              </p>
              <p className="logo-dropzone-hint">PNG · JPG · WebP · بحد أقصى 5 ميغابايت · يُعرض بحجم 512×512</p>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="logo-file-input"
                onChange={handleLogoInputChange}
                disabled={logoUploading}
              />
            </div>

            {currentLogoUrl && !logoPreview && (
              <button
                type="button"
                className="logo-delete-btn"
                onClick={handleDeleteLogo}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14H6L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4h6v2" />
                </svg>
                حذف الشعار
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="gallery-section">
        <h2>معرض الصور (الألبوم)</h2>
        <p className="gallery-section-hint">
          {galleryPhotos.length} / {MAX_GALLERY_PHOTOS} صورة — تظهر في أسفل صفحة الصالون
        </p>

        {galleryPhotos.length > 0 && (
          <div className="gallery-grid">
            {galleryPhotos.map((photo) => (
              <div key={photo.id} className="gallery-item">
                <img
                  src={`${API_BASE_URL}/files/gallery/${accountId}/${photo.id}`}
                  alt=""
                  className="gallery-item-img"
                />
                <button
                  type="button"
                  className="gallery-item-delete"
                  onClick={() => handleDeleteGalleryPhoto(photo.id)}
                  aria-label="حذف الصورة"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {galleryPhotos.length < MAX_GALLERY_PHOTOS && (
          <div
            className={`gallery-dropzone${galleryDragging ? ' dragging' : ''}`}
            onClick={() => galleryInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setGalleryDragging(true); }}
            onDragLeave={() => setGalleryDragging(false)}
            onDrop={handleGalleryDrop}
            role="button"
            tabIndex={0}
            aria-label="منطقة رفع صور المعرض"
            onKeyDown={(e) => e.key === 'Enter' && galleryInputRef.current?.click()}
          >
            <svg className="gallery-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="3" y="3" width="18" height="18" rx="3" />
              <path d="M3 15l5-5 4 4 3-3 6 6" />
              <circle cx="8.5" cy="8.5" r="1.5" />
            </svg>
            <p className="gallery-dropzone-text">
              {galleryUploading ? 'جاري الرفع...' : 'اسحب صوراً هنا أو اضغط للاختيار'}
            </p>
            <p className="gallery-dropzone-hint">PNG · JPG · WebP · بحد أقصى 5 ميغابايت لكل صورة</p>
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="gallery-file-input"
              onChange={handleGalleryInputChange}
              disabled={galleryUploading}
              multiple
            />
          </div>
        )}
      </section>
    </div>
  );
}
